/**
 * Vehicle Inventory page — the stock-record ledger (source of truth), read view.
 * A lean, paginated, sortable, searchable table of the dealer group's inventory.
 * Table styling mirrors components/audit/AuditTable.tsx (headerCellStyle ~L205,
 * dataCellStyle ~L238); page header mirrors pages/photos/DailyPhotoListPage.tsx.
 * Group-scoped via useDealerGroup(); a concrete group is required.
 */
import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import { ChangeTypeChip, fieldLabel, formatChangeTime, formatChangeValue } from "../../components/inventory/changeFormat"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { THEME } from "../../config/theme"
import {
  getInventoryFilterOptions,
  getInventoryFreshness,
  listInventoryChanges,
  listInventoryRecords,
  InventoryChange,
  InventoryFilterOptions,
  InventoryRecord,
} from "../../api/inventory"
import InventoryFiltersModal, {
  countActiveFilters,
  EMPTY_FILTER,
  FilterState,
} from "../../components/inventory/InventoryFiltersModal"
import VehicleDetailModal from "../../components/inventory/VehicleDetailModal"
import { getPreferences, updatePreference } from "../../api/preferences"

const PAGE_SIZE = 50
const DEFAULT_SORT_FIELD = "last_modified_date"
const FILTER_PREF_KEY = "inventory_filters"

type SortDirection = "asc" | "desc"

interface ColumnDef {
  key: string // doubles as the backend sort field
  label: string
  align?: "left" | "right"
  mono?: boolean
  format: (record: InventoryRecord) => string
}

// Default column set + order (plan §9). Every other promoted field is hidden by
// default; the show/hide + reorder control is a later phase.
const COLUMNS: ColumnDef[] = [
  { key: "rooftop", label: "Rooftop", format: (r) => r.rooftop ?? "" },
  { key: "vehicle_type", label: "Type", format: (r) => r.vehicle_type ?? "" },
  { key: "tekion_status", label: "Status", format: (r) => r.tekion_status ?? "" },
  { key: "age_days", label: "Age", align: "right", format: (r) => formatInt(r.age_days) },
  { key: "vin", label: "VIN", mono: true, format: (r) => r.vin ?? "" },
  { key: "stock_number", label: "Stock #", mono: true, format: (r) => r.stock_number ?? "" },
  { key: "year", label: "Year", align: "right", format: (r) => (r.year != null ? String(r.year) : "") },
  { key: "make", label: "Make", format: (r) => r.make ?? "" },
  { key: "model", label: "Model", format: (r) => r.model ?? "" },
  { key: "color_ext", label: "Ext Color", format: (r) => r.color_ext ?? "" },
  { key: "odometer", label: "Odometer", align: "right", format: (r) => formatInt(r.odometer) },
  { key: "price_invoice", label: "Invoice", align: "right", format: (r) => formatMoney(r.price_invoice) },
  { key: "last_modified_date", label: "Last Modified", format: (r) => r.last_modified_date ?? "" },
  { key: "creation_date", label: "Created", format: (r) => r.creation_date ?? "" },
  { key: "stocked_in_date", label: "Stocked In", format: (r) => r.stocked_in_date ?? "" },
  { key: "received_date", label: "Received", format: (r) => r.received_date ?? "" },
  { key: "sold_date", label: "Sold", format: (r) => r.sold_date ?? "" },
]

function formatInt(value: number | null): string {
  return value == null ? "" : value.toLocaleString()
}

function formatMoney(value: number | null): string {
  if (value == null) return ""
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

function formatFreshness(iso: string | null): string {
  if (!iso) return "never"
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString()
}

export default function VehicleInventoryPage() {
  return (
    <Layout>
      <VehicleInventoryPageContent />
    </Layout>
  )
}

function VehicleInventoryPageContent() {
  const { dealerGroupId } = useDealerGroup()

  const [records, setRecords] = useState<InventoryRecord[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD)
  const [sortDir, setSortDir] = useState<SortDirection>("desc")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)
  const [refreshKey] = useState(0)
  const [activeTab, setActiveTab] = useState<"inventory" | "changes">("inventory")
  const [appliedFilter, setAppliedFilter] = useState<FilterState>(EMPTY_FILTER)
  const [filterOptions, setFilterOptions] = useState<InventoryFilterOptions | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [prefsLoaded, setPrefsLoaded] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<InventoryRecord | null>(null)

  const hasGroup = dealerGroupId != null
  const activeFilterCount = countActiveFilters(appliedFilter)

  // Debounce the search box, and reset to the first page whenever it changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Restore the saved filter once on mount (gates the records fetch below so it
  // doesn't fire with an empty filter first).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const prefs = await getPreferences()
        const saved = prefs[FILTER_PREF_KEY]
        if (!cancelled && saved) {
          setAppliedFilter({ ...EMPTY_FILTER, ...JSON.parse(saved) })
        }
      } catch {
        // A missing/unreadable preference just means "no saved filter".
      } finally {
        if (!cancelled) setPrefsLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Load the filter options for the Filters modal when the active group changes.
  useEffect(() => {
    if (!hasGroup) return
    let cancelled = false
    ;(async () => {
      try {
        const options = await getInventoryFilterOptions(dealerGroupId)
        if (!cancelled) setFilterOptions(options)
      } catch {
        // Non-critical: the modal degrades to empty option lists.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, hasGroup])

  // The freshness stamp — refetched when the active group changes.
  useEffect(() => {
    if (!hasGroup) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await getInventoryFreshness(dealerGroupId)
        if (!cancelled) setLastUpdatedAt(response.last_updated_at)
      } catch {
        // Non-critical: the table's own error surface covers real failures.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, hasGroup, refreshKey])

  // The page of records — refetched on group / page / sort / search / filter change.
  useEffect(() => {
    if (!hasGroup || !prefsLoaded) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const response = await listInventoryRecords({
          dealerGroupId,
          page,
          size: PAGE_SIZE,
          sort: `${sortField}:${sortDir}`,
          search: search || undefined,
          rooftop: appliedFilter.rooftop,
          status: appliedFilter.status,
          vehicleType: appliedFilter.vehicleType,
          website: appliedFilter.website,
          yearMin: appliedFilter.yearMin,
          yearMax: appliedFilter.yearMax,
          ageMin: appliedFilter.ageMin,
          ageMax: appliedFilter.ageMax,
        })
        if (!cancelled) {
          setRecords(response.records)
          setTotal(response.total)
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load inventory")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, hasGroup, prefsLoaded, page, sortField, sortDir, search, refreshKey, appliedFilter])

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
      setSortDir("asc")
    }
    setPage(1)
  }

  const applyFilters = (filter: FilterState) => {
    setAppliedFilter(filter)
    setPage(1)
    setFiltersOpen(false)
    void updatePreference(FILTER_PREF_KEY, JSON.stringify(filter))
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const lastRow = Math.min(page * PAGE_SIZE, total)

  if (!hasGroup) {
    return <DealerGroupRequiredNotice surface="the vehicle inventory" />
  }

  return (
    <div>
      <div style={headerBarStyle}>
        <div>
          <h1 style={titleStyle}>Vehicle Inventory</h1>
          <div style={subtitleStyle}>
            {total.toLocaleString()} records · data as of {formatFreshness(lastUpdatedAt)}
          </div>
        </div>
        <div style={{ display: "flex", gap: THEME.spacing.sm, alignItems: "center" }}>
          <Link to="/inventory/update" style={updateLinkStyle}>Update Inventory</Link>
        </div>
      </div>

      <div style={tabStripStyle}>
        <button style={tabStyle(activeTab === "inventory")} onClick={() => setActiveTab("inventory")}>
          Inventory
        </button>
        <button style={tabStyle(activeTab === "changes")} onClick={() => setActiveTab("changes")}>
          Changes
        </button>
      </div>

      {activeTab === "inventory" ? (
        <>
          <div style={{ marginBottom: THEME.spacing.md, display: "flex", gap: THEME.spacing.sm, alignItems: "center" }}>
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search VIN, stock, make, model…"
              style={searchInputStyle}
            />
            <button style={filtersButtonStyle} onClick={() => setFiltersOpen(true)}>
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
            {activeFilterCount > 0 && (
              <button style={clearFiltersLinkStyle} onClick={() => applyFilters(EMPTY_FILTER)}>
                Clear
              </button>
            )}
          </div>

          <InventoryTable
            records={records}
            loading={loading}
            error={error}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            onRowClick={setSelectedRecord}
          />

          <div style={paginationBarStyle}>
            <span style={{ color: THEME.colors.neutral.light.textSecondary, fontSize: 13 }}>
              {total === 0 ? "No records" : `Showing ${firstRow.toLocaleString()}–${lastRow.toLocaleString()} of ${total.toLocaleString()}`}
            </span>
            <div style={{ display: "flex", gap: THEME.spacing.sm, alignItems: "center" }}>
              <button style={pageButtonStyle(page <= 1)} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Prev
              </button>
              <span style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
                Page {page.toLocaleString()} of {totalPages.toLocaleString()}
              </span>
              <button style={pageButtonStyle(page >= totalPages)} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <ChangesPanel dealerGroupId={dealerGroupId} refreshKey={refreshKey} />
      )}

      <InventoryFiltersModal
        open={filtersOpen}
        options={filterOptions}
        initial={appliedFilter}
        onApply={applyFilters}
        onClose={() => setFiltersOpen(false)}
      />

      <VehicleDetailModal record={selectedRecord} onClose={() => setSelectedRecord(null)} />
    </div>
  )
}

interface TableProps {
  records: InventoryRecord[]
  loading: boolean
  error: string | null
  sortField: string
  sortDir: SortDirection
  onSort: (field: string) => void
  onRowClick: (record: InventoryRecord) => void
}

function InventoryTable({ records, loading, error, sortField, sortDir, onSort, onRowClick }: TableProps) {
  const sortIndicator = (field: string) => {
    if (field !== sortField) return ""
    return sortDir === "asc" ? " ▲" : " ▼"
  }

  const body = useMemo(() => {
    if (loading) return <StatusRow message="Loading inventory…" />
    if (error) return <StatusRow message={error} isError />
    if (records.length === 0) return <StatusRow message="No vehicles match your search." />
    return records.map((record, rowIndex) => {
      const rowBackground = rowIndex % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain
      return (
        <tr key={record.id} onClick={() => onRowClick(record)} style={{ cursor: "pointer" }}>
          {COLUMNS.map((col) => (
            <td key={col.key} style={dataCellStyle(col, rowBackground)}>
              {col.format(record)}
            </td>
          ))}
        </tr>
      )
    })
  }, [records, loading, error, onRowClick])

  return (
    <div style={tableScrollStyle}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                style={headerCellStyle(col)}
                onClick={() => onSort(col.key)}
                title="Click to sort"
              >
                {col.label}
                {sortIndicator(col.key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  )
}

function StatusRow({ message, isError = false }: { message: string; isError?: boolean }) {
  return (
    <tr>
      <td
        colSpan={COLUMNS.length}
        style={{
          padding: THEME.spacing.lg,
          textAlign: "center",
          fontSize: 13,
          color: isError ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textSecondary,
          border: `1px solid ${THEME.auditTable.borderColor}`,
        }}
      >
        {message}
      </td>
    </tr>
  )
}

// --- Changes tab (the change report) ---

const CHANGES_PAGE_SIZE = 50
const CHANGE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All changes" },
  { value: "arrival", label: "Arrivals" },
  { value: "sold", label: "Sold" },
  { value: "unwind", label: "Unwinds" },
  { value: "transfer", label: "Transfers" },
  { value: "field_change", label: "Field changes" },
]
const CHANGE_COLUMNS = ["Time", "Type", "VIN", "Stock #", "Field", "Change"]

function ChangesStatusRow({ message, isError = false }: { message: string; isError?: boolean }) {
  return (
    <tr>
      <td
        colSpan={CHANGE_COLUMNS.length}
        style={{
          padding: THEME.spacing.lg,
          textAlign: "center",
          fontSize: 13,
          color: isError ? THEME.colors.semantic.danger : THEME.colors.neutral.light.textSecondary,
          border: `1px solid ${THEME.auditTable.borderColor}`,
        }}
      >
        {message}
      </td>
    </tr>
  )
}

interface ChangesPanelProps {
  dealerGroupId: number | null
  refreshKey: number
}

function ChangesPanel({ dealerGroupId, refreshKey }: ChangesPanelProps) {
  const [changes, setChanges] = useState<InventoryChange[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const response = await listInventoryChanges({
          dealerGroupId,
          page,
          size: CHANGES_PAGE_SIZE,
          changeType: typeFilter || undefined,
        })
        if (!cancelled) {
          setChanges(response.changes)
          setTotal(response.total)
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load changes")
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, page, typeFilter, refreshKey])

  const totalPages = Math.max(1, Math.ceil(total / CHANGES_PAGE_SIZE))

  return (
    <div>
      <div style={{ marginBottom: THEME.spacing.md, display: "flex", gap: THEME.spacing.sm, alignItems: "center" }}>
        <span style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>Show:</span>
        <select
          value={typeFilter}
          onChange={(event) => {
            setTypeFilter(event.target.value)
            setPage(1)
          }}
          style={selectStyle}
        >
          {CHANGE_TYPE_OPTIONS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div style={tableScrollStyle}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {CHANGE_COLUMNS.map((label) => (
                <th key={label} style={changesHeaderCellStyle}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <ChangesStatusRow message="Loading changes…" />
            ) : error ? (
              <ChangesStatusRow message={error} isError />
            ) : changes.length === 0 ? (
              <ChangesStatusRow message="No changes recorded yet — upload a newer file to start the change log." />
            ) : (
              changes.map((change, index) => {
                const rowBackground = index % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain
                return (
                  <tr key={change.id}>
                    <td style={changesDataCellStyle(rowBackground)}>{formatChangeTime(change.detected_at)}</td>
                    <td style={changesDataCellStyle(rowBackground)}>
                      <ChangeTypeChip type={change.change_type} />
                    </td>
                    <td style={changesDataCellStyle(rowBackground, true)}>{change.vin ?? ""}</td>
                    <td style={changesDataCellStyle(rowBackground)}>{change.stock_number ?? ""}</td>
                    <td style={changesDataCellStyle(rowBackground)}>{fieldLabel(change.field)}</td>
                    <td style={changesDataCellStyle(rowBackground)}>
                      {change.change_type === "field_change"
                        ? `${formatChangeValue(change.field, change.old_value)} → ${formatChangeValue(change.field, change.new_value)}`
                        : change.change_type === "cleared"
                        ? `removed at source — kept ${formatChangeValue(change.field, change.old_value)}`
                        : change.new_value ?? ""}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={paginationBarStyle}>
        <span style={{ color: THEME.colors.neutral.light.textSecondary, fontSize: 13 }}>
          {total === 0 ? "No changes" : `${total.toLocaleString()} changes`}
        </span>
        <div style={{ display: "flex", gap: THEME.spacing.sm, alignItems: "center" }}>
          <button style={pageButtonStyle(page <= 1)} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Prev
          </button>
          <span style={{ fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
            Page {page.toLocaleString()} of {totalPages.toLocaleString()}
          </span>
          <button style={pageButtonStyle(page >= totalPages)} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Styles (tokens copied from AuditTable.tsx + DailyPhotoListPage.tsx) ---

const headerBarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.md,
  gap: THEME.spacing.md,
  flexWrap: "wrap",
}

const titleStyle: React.CSSProperties = {
  fontSize: THEME.typography.h3.size,
  fontWeight: THEME.typography.h3.weight,
  letterSpacing: THEME.typography.h3.tracking,
  color: THEME.colors.text,
  margin: 0,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.xs,
}

const searchInputStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 13,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.border}`,
  fontFamily: THEME.fonts.primary,
  minWidth: 260,
}

const tableScrollStyle: React.CSSProperties = {
  overflowX: "auto",
  border: `1px solid ${THEME.auditTable.borderColor}`,
  borderRadius: THEME.borderRadius.sm,
}

function headerCellStyle(col: ColumnDef): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: THEME.colors.white,
    textAlign: col.align === "right" ? "right" : "left",
    backgroundColor: THEME.auditTable.headerBackground,
    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
    boxSizing: "border-box",
    position: "sticky",
    top: 0,
    zIndex: 2,
    cursor: "pointer",
    whiteSpace: "nowrap",
  }
}

function dataCellStyle(col: ColumnDef, rowBackground: string): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 12,
    color: THEME.colors.neutral.light.textPrimary,
    textAlign: col.align === "right" ? "right" : "left",
    backgroundColor: rowBackground,
    borderRight: `1px solid ${THEME.auditTable.borderColor}`,
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    fontFamily: col.mono ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' : "inherit",
  }
}

const paginationBarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: THEME.spacing.md,
  gap: THEME.spacing.md,
  flexWrap: "wrap",
}

function pageButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
    fontSize: 13,
    fontWeight: 500,
    color: THEME.colors.white,
    background: THEME.colors.navy,
    border: "none",
    borderRadius: THEME.borderRadius.md,
    fontFamily: THEME.fonts.primary,
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.5 : 1,
  }
}

const updateLinkStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.white,
  background: THEME.colors.navy,
  borderRadius: THEME.borderRadius.md,
  fontFamily: THEME.fonts.primary,
  textDecoration: "none",
  whiteSpace: "nowrap",
}

const filtersButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.navy,
  background: THEME.colors.white,
  border: `1px solid ${THEME.colors.navy}`,
  borderRadius: THEME.borderRadius.md,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
  whiteSpace: "nowrap",
}

const clearFiltersLinkStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: THEME.colors.neutral.light.textSecondary,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
  textDecoration: "underline",
}

const tabStripStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.md,
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  marginBottom: THEME.spacing.md,
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    color: active ? THEME.colors.text : THEME.colors.neutral.light.textSecondary,
    background: "transparent",
    border: "none",
    borderBottom: `3px solid ${active ? THEME.colors.navy : "transparent"}`,
    marginBottom: -1, // overlap the strip's own border
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
  }
}

const selectStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 13,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.border}`,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
}

const changesHeaderCellStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.white,
  textAlign: "left",
  backgroundColor: THEME.auditTable.headerBackground,
  borderRight: "1px solid rgba(255, 255, 255, 0.1)",
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  boxSizing: "border-box",
  whiteSpace: "nowrap",
}

function changesDataCellStyle(rowBackground: string, mono = false): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 12,
    color: THEME.colors.neutral.light.textPrimary,
    textAlign: "left",
    backgroundColor: rowBackground,
    borderRight: `1px solid ${THEME.auditTable.borderColor}`,
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    fontFamily: mono ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' : "inherit",
  }
}
